import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // OG tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    if (canonical) {
      updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    }

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Canonical link
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', `${siteUrl}${canonical}`);
    }

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Schema.org JSON-LD
    if (schema) {
      let scriptElement = document.querySelector('script[data-page-schema]');
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('data-page-schema', 'true');
        document.head.appendChild(scriptElement);
      }
      
      // Replace VITE_SITE_URL placeholder with actual value
      const schemaString = JSON.stringify(schema, null, 2).replace(
        /VITE_SITE_URL/g,
        siteUrl
      );
      scriptElement.textContent = schemaString;
    }

    return () => {
      // Cleanup page-specific schema
      const scriptElement = document.querySelector('script[data-page-schema]');
      if (scriptElement) {
        scriptElement.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, schema]);
};
